// Per-user mailbox visibility (Inbox mailbox picker). Pure helpers — no localStorage here;
// that lives in userPrefs.ts's useHiddenMailboxes hook, which uses these for parse/serialize.

/**
 * Filter out groups whose mailbox address is in the hidden set (case-insensitive).
 * Generic over the field name so it works against InboxGroup's `mailbox_address`
 * without an intermediate remap — pass the real field name as needed via the type param.
 */
export function filterHiddenGroups<T extends { mailbox_address: string }>(groups: T[], hidden: Set<string>): T[] {
  if (hidden.size === 0) return groups;
  const lower = new Set(Array.from(hidden, (h) => h.toLowerCase()));
  return groups.filter((g) => !lower.has(g.mailbox_address.toLowerCase()));
}

/** Parse a stored hidden-mailbox list. Tolerant of null/garbage input — always returns a Set. */
export function parseHiddenList(raw: string | null): Set<string> {
  if (!raw) return new Set();
  try {
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return new Set();
    return new Set(
      parsed.filter((v): v is string => typeof v === "string").map((v) => v.toLowerCase())
    );
  } catch {
    return new Set();
  }
}

/** Serialize a hidden-mailbox set back to its stored JSON form. */
export function serializeHiddenList(set: Set<string>): string {
  return JSON.stringify(Array.from(set));
}
